import { Theme } from 'config/theme';
import moment from 'moment';
import Calendar from 'rc-calendar';
import 'rc-calendar/assets/index.css';
import enUS from 'rc-calendar/lib/locale/en_US';
import zhCN from 'rc-calendar/lib/locale/zh_CN';
import Picker from 'rc-calendar/lib/Picker';
import React, { useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  min-width: 231px;
  width: 231px;
`;

const NavigationButton = styled.button`
  background: white;
  border: 1px solid #e4e4e7;
  border-radius: 6px;
  min-height: 36px;
  padding: 8px 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
    border-color: #d4d4d8;
  }

  &:active {
    background-color: #efefef;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  img {
    width: 16px;
    height: 16px;
    display: block;
  }
`;

const DateInputWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DateInput = styled.div`
  background: white;
  border: 1px solid #e4e4e7;
  border-radius: 6px;
  min-height: 36px;
  padding: 8px 16px;
  display: flex;
  gap: 8px;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;

  &:hover {
    background-color: #f5f5f5;
    border-color: #d4d4d8;
  }

  &:focus-within {
    border-color: ${Theme.PrimaryBlue || '#3b82f6'};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const DateText = styled.span`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #09090b;
  white-space: nowrap;
`;

const CalendarIcon = styled.div`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
  }
`;

interface IDateNavigationPickerProps {
  value?: moment.Moment;
  onChange?: (date: moment.Moment) => void;
  /** Optional callback specifically for returning start and end dates in weekly mode */
  onChangeRange?: (startDate: moment.Moment, endDate: moment.Moment) => void;
  /** Optional callback specifically for returning the selected month */
  onChangeMonth?: (monthDate: moment.Moment) => void;
  minDate?: moment.Moment;
  maxDate?: moment.Moment;
  format?: string;
  disabled?: boolean;
  onDateChange?: (date: moment.Moment) => void;
  hideNavigationButtons?: boolean;
  /** Set to true to enable week range navigation and display */
  isWeeklyMode?: boolean;
  /** Set to true to enable month navigation and display (e.g. "August") */
  isMonthlyMode?: boolean;
}

const format = 'MMM DD, YYYY';
const cn = typeof location !== 'undefined' && location.search.indexOf('cn') !== -1;
const now = moment();
if (cn) {
  now.locale('zh-cn').utcOffset(8);
} else {
  now.locale('en-gb').utcOffset(0);
}

export const DateNavigationPicker = React.forwardRef<HTMLDivElement, IDateNavigationPickerProps>(
  (props, ref) => {
    const {
      value = moment(),
      onChange,
      onChangeRange,
      onChangeMonth,
      minDate,
      maxDate,
      disabled = false,
      onDateChange,
      hideNavigationButtons = false,
      isWeeklyMode = false,
      isMonthlyMode = false,
    } = props;

    const [open, setOpen] = useState<boolean>(false);
    const [currentDate, setCurrentDate] = useState<moment.Moment>(value);

    // Determine active mode (Monthly takes precedence over Weekly)
    const mode = isMonthlyMode ? 'month' : isWeeklyMode ? 'week' : 'day';

    // Calculate ranges based on mode
    const weekStartDate = currentDate.clone().startOf('isoWeek');
    const weekEndDate = currentDate.clone().endOf('isoWeek');
    const monthStartDate = currentDate.clone().startOf('month');

    // Helper to handle callbacks based on mode
    const triggerCallbacks = (date: moment.Moment) => {
      if (mode === 'month') {
        const mStart = date.clone().startOf('month');
        if (onChangeMonth) {
          onChangeMonth(mStart);
        } else {
          onChange?.(mStart);
          onDateChange?.(mStart);
        }
      } else if (mode === 'week') {
        const wStart = date.clone().startOf('isoWeek');
        const wEnd = date.clone().endOf('isoWeek');
        if (onChangeRange) {
          onChangeRange(wStart, wEnd);
        } else {
          onChange?.(wStart);
          onDateChange?.(wStart);
        }
      } else {
        onChange?.(date);
        onDateChange?.(date);
      }
    };

    const handlePrevious = () => {
      const duration = mode === 'month' ? 'month' : mode === 'week' ? 'week' : 'day';
      const newDate = currentDate.clone().subtract(1, duration);

      if (!minDate || newDate.isSameOrAfter(minDate)) {
        setCurrentDate(newDate);
        triggerCallbacks(newDate);
      }
    };

    const handleNext = () => {
      const duration = mode === 'month' ? 'month' : mode === 'week' ? 'week' : 'day';
      const newDate = currentDate.clone().add(1, duration);

      if (!maxDate || newDate.isSameOrBefore(maxDate)) {
        setCurrentDate(newDate);
        triggerCallbacks(newDate);
      }
    };

    const handleDateChange = (newDate: moment.Moment) => {
      setCurrentDate(newDate);
      setOpen(false);
      triggerCallbacks(newDate);
    };

    const handleOpenChange = (isOpen: boolean) => {
      setOpen(isOpen);
    };

    const calendar = (
      <Calendar
        locale={cn ? zhCN : enUS}
        format={format}
        defaultValue={currentDate}
        value={currentDate}
        style={{ zIndex: 1000 }}
        dateInputPlaceholder="Select Date"
        showDateInput={false}
        disabledDate={(current) => {
          if (minDate && current?.isBefore(minDate, 'day')) return true;
          if (maxDate && current?.isAfter(maxDate, 'day')) return true;
          return false;
        }}
      />
    );

    const duration = mode === 'month' ? 'month' : mode === 'week' ? 'week' : 'day';
    const canGoPrevious =
      !minDate || currentDate.clone().subtract(1, duration).isSameOrAfter(minDate);
    const canGoNext = !maxDate || currentDate.clone().add(1, duration).isSameOrBefore(maxDate);

    // Determine what text to display based on the mode
    let displayText = '';
    if (mode === 'month') {
      // Format 'MMMM' outputs just "August". You can change to 'MMMM YYYY' if you want "August 2024"
      displayText = currentDate.format('MMMM');
    } else if (mode === 'week') {
      displayText = `${weekStartDate.format(format)} - ${weekEndDate.format(format)}`;
    } else {
      displayText = currentDate.format(format);
    }

    return (
      <Container ref={ref}>
        {!hideNavigationButtons && (
          <NavigationButton
            onClick={handlePrevious}
            disabled={disabled || !canGoPrevious}
            aria-label="Previous date"
            title="Previous date"
          >
            <i className="saf-arrow-left-2" />
          </NavigationButton>
        )}

        <DateInputWrapper>
          <Picker
            onOpenChange={handleOpenChange}
            open={open}
            animation="slide-up"
            calendar={calendar}
            value={currentDate}
            onChange={handleDateChange}
          >
            {() => (
              <DateInput onClick={() => !disabled && setOpen(!open)}>
                <CalendarIcon>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect
                      x="3"
                      y="4"
                      width="18"
                      height="18"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path d="M3 9H21" stroke="currentColor" strokeWidth="2" />
                    <path d="M7 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M17 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </CalendarIcon>
                <DateText>{displayText}</DateText>
              </DateInput>
            )}
          </Picker>
        </DateInputWrapper>

        {!hideNavigationButtons && (
          <NavigationButton
            onClick={handleNext}
            disabled={disabled || !canGoNext}
            aria-label="Next date"
            title="Next date"
          >
            <i className="saf-arrow-right-3" />
          </NavigationButton>
        )}
      </Container>
    );
  },
);

DateNavigationPicker.displayName = 'DateNavigationPicker';
